import axios from 'axios';
import { computed, ref } from 'vue';

/**
 * Headers that tag a request as editor-originated.
 *
 * - `Accept: application/json` makes an expired web session return a real 401
 *   instead of a 302 redirect to the login page (which would be silently
 *   followed and look like a successful save).
 * - `X-Editor` lets the global axios interceptor scope session-expiry detection
 *   to editor requests only, so unrelated 401/419s elsewhere don't raise the
 *   banner.
 */
export const EDITOR_REQUEST_HEADERS = {
    Accept: 'application/json',
    'X-Editor': '1',
} as const;

/**
 * The heartbeat is a silent background poll. It still carries `X-Editor` so a
 * 401/419 raises the banner, but `X-Heartbeat` tells the global interceptor to
 * stay quiet on any other failure (403 from an expired trial, 500, offline) so
 * the editor isn't spammed with an error toast every poll.
 */
export const HEARTBEAT_REQUEST_HEADERS = {
    ...EDITOR_REQUEST_HEADERS,
    'X-Heartbeat': '1',
} as const;

const sessionExpired = ref(false);
const hasPendingSave = ref(false);
const isRetrying = ref(false);

/**
 * A 401 (unauthenticated) or 419 (expired CSRF / session) both mean the editor
 * session can no longer save.
 */
export const isSessionAuthError = (status?: number): boolean => {
    return status === 401 || status === 419;
};

/**
 * The session died while a save was attempted (or a heartbeat detected it).
 * We also remember there is unsaved work so the banner can't be dismissed by a
 * background heartbeat silently restoring auth.
 */
export const markSessionExpired = (): void => {
    sessionExpired.value = true;
    hasPendingSave.value = true;
};

/**
 * Auth is back, but we deliberately keep `hasPendingSave` so the banner stays
 * visible (now green) until the user actually re-saves their work.
 */
export const markSessionRestored = (): void => {
    sessionExpired.value = false;
};

/** A save succeeded - clear everything and hide the banner. */
export const markSaved = (): void => {
    sessionExpired.value = false;
    hasPendingSave.value = false;
};

export default function useEditorSession(pingUrl?: string) {
    const bannerVisible = computed(
        () => sessionExpired.value || hasPendingSave.value
    );

    /**
     * Heartbeat. Resolves `true` when the session is valid. On a 401/419 the
     * global interceptor has already flagged the session as expired, so we just
     * report failure here.
     */
    const ping = async (): Promise<boolean> => {
        if (!pingUrl) {
            return false;
        }

        try {
            await axios.get(pingUrl, { headers: HEARTBEAT_REQUEST_HEADERS });
            markSessionRestored();

            return true;
        } catch {
            return false;
        }
    };

    return {
        sessionExpired,
        hasPendingSave,
        isRetrying,
        bannerVisible,
        ping,
        markSessionExpired,
        markSessionRestored,
        markSaved,
    };
}
